<script setup lang="ts">
  import { onMounted, ref, watch } from 'vue'
  import {
    ANIMATION_DELAY_MS,
    DEFAULT_DARK_WIDTH,
    DEFAULT_LIGHT_WIDTH,
    ONE_HUNDRED_PERCENT,
  } from './ProgressBar.constants'
  import { calculatePercentage } from './ProgressBar.utils'
  import {
    DEFAULT_FLOW_CURRENT_STEP,
    DEFAULT_FLOW_FIRST_STEP,
    DEFAULT_FLOW_STEPS_COUNT,
  } from '@/shared/constants/flow.constants'

  export interface Props {
    stepsCount: number
    currentStep: number
    firstStep: number
  }

  const props = withDefaults(defineProps<Props>(), {
    stepsCount: DEFAULT_FLOW_STEPS_COUNT,
    currentStep: DEFAULT_FLOW_CURRENT_STEP,
    firstStep: DEFAULT_FLOW_FIRST_STEP,
  })

  const darkWidth = ref<string>(DEFAULT_DARK_WIDTH)
  const lightWidth = ref<string>(DEFAULT_LIGHT_WIDTH)
  const stepWidth = ONE_HUNDRED_PERCENT / props.stepsCount

  const setProgressForStep = (step: number) => {
    let safeStep = step
    if (safeStep < 0) safeStep = 0
    if (safeStep >= props.stepsCount) safeStep = props.stepsCount
    darkWidth.value = calculatePercentage(safeStep, stepWidth)

    const lightStep = safeStep === 0 ? 0 : step + 1
    lightWidth.value = calculatePercentage(lightStep, stepWidth)
  }

  const updateProgress = () => {
    setTimeout(() => {
      setProgressForStep(props.currentStep)
    }, ANIMATION_DELAY_MS)
  }

  onMounted(() => {
    setProgressForStep(props.firstStep)
    updateProgress()
  })

  watch(
    () => props.currentStep,
    () => {
      updateProgress()
    }
  )
</script>

<template>
  <div class="flow-progress">
    <div class="flow-progress__scale">
      <div
        class="flow-progress__light"
        :style="{
          width: lightWidth,
        }"
      />
      <div
        class="flow-progress__dark"
        :style="{
          width: darkWidth,
        }"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
  @import '@/shared/assets/styles/utils';

  .flow-progress {
    max-width: $flow-width;
    margin-inline: auto;
    width: 100%;

    @include mobile-flow {
      width: calc(100% - 32px);
    }

    .flow-progress__scale {
      background: rgb(82 108 130 / 10%);
      width: 100%;
      height: 8px;
      border-radius: 100px;
      overflow: hidden;
      position: relative;
    }

    .flow-progress__light {
      border-radius: 50px;
      height: 100%;
      position: absolute;
      left: 0;
      top: 0;
      font-size: 0;
      transition:
        1s,
        background 0.15s ease;
      background: #9bf1ad;
    }

    .flow-progress__dark {
      border-radius: 50px;
      height: 100%;
      position: absolute;
      left: 0;
      top: 0;
      font-size: 0;
      transition:
        1s,
        background 0.15s ease;
      background: #57c863;
    }
  }
</style>
